import { FieldPosition, TemplatePage, DataLayoutTab, DataLayoutSection, DataLayoutPosition, TemplateField } from '../../../../types/template-types';
import { FormEditorState } from '../../../formeditor-types';
import { isVisible } from '../../../helpers';
import { ViewPosition } from '../../../requiredFields';
import { getField } from '../../../selectors';

function fieldPositionSorter(a: FieldPosition, b: FieldPosition) {
  const compare1 = a.y - b.y;
  if (compare1 !== 0) {
    return compare1;
  }

  const compare2 = a.x - b.x;
  return compare2;
}

export function viewPositionSorter(a: ViewPosition, b: ViewPosition) {
  if (a.groupType !== b.groupType) {
    if (a.groupType === 'primary') {
      return -1;
    } else if (b.groupType === 'primary') {
      return 1;
    } else if (a.groupType === 'secondary') {
      return -1;
    } else if (b.groupType === 'secondary') {
      return 1;
    }
  }
  if (a.fieldPosition != null) {
    return formViewLocationSorter(a, b);
  }
  if (a.dataLayoutPosition != null) {
    return listViewLocationSorter(a, b);
  }

  return a.field?.name.localeCompare(b.field.name.toLowerCase());
}

function formViewLocationSorter(a: ViewPosition, b: ViewPosition) {
  const compare1 = a.page.number - b.page.number;
  if (compare1 !== 0) {
    return compare1;
  }
  const compare2 = fieldPositionSorter(a.fieldPosition, b.fieldPosition);
  return compare2;
}

function listViewLocationSorter(a: ViewPosition, b: ViewPosition) {
  const compare1 = a.tab.sortNumber - b.tab.sortNumber;
  if (compare1 !== 0) {
    return compare1;
  }

  const compare2 = a.section.sortNumber - b.section.sortNumber;
  if (compare2 !== 0) {
    return compare2;
  }

  const compare3 = a.dataLayoutPosition.sortNumber - b.dataLayoutPosition.sortNumber;
  return compare3;
}

export function getViewPositionForFields(state: FormEditorState, fieldIds: string[]) {
  const isPublicForm = !!state.sharedAccessToken;

  if (!fieldIds || fieldIds.length === 0) {
    return [];
  }

  const visibleSortedPages = state.template.pages ? getSortedPages(state.template.pages, state.formulaValues) : [];
  const visibleSortedTabs =
    state.template.dataLayouts && state.template.dataLayouts.length > 0 && state.template.dataLayouts[0].dataLayoutTabs
      ? getSortedTabs(state.template.dataLayouts[0].dataLayoutTabs, state.formulaValues)
      : [];

  const viewPositions: ViewPosition[] = [];

  for (const fieldId of fieldIds) {
    const field = getField(state.template, fieldId);
    const viewPosition = getViewPosition(state.view, visibleSortedTabs, visibleSortedPages, field, state.formulaValues, isPublicForm);

    viewPositions.push(viewPosition);
  }

  return viewPositions.sort(viewPositionSorter);
}

export function getSortedPages(pages: TemplatePage[], formulaValues: Record<string, any>) {
  return pages.filter((x) => isVisible(formulaValues, x)).sort((a, b) => a.number - b.number);
}

export function getSortedTabs(tabs: DataLayoutTab[], formulaValues: Record<string, any>) {
  return tabs.filter((x) => isVisible(formulaValues, x)).sort((a, b) => a.sortNumber - b.sortNumber);
}

export function getVisiblePageNumber(page: TemplatePage, visibleSortedPages: TemplatePage[]) {
  return visibleSortedPages.findIndex((x) => x.id === page.id) + 1;
}

function getFormViewPosition(
  visibleSortedPages: TemplatePage[],
  fieldId: string,
  formulaValues: Record<string, any>
): { page: TemplatePage; fieldPosition: FieldPosition } {
  for (const page of visibleSortedPages.filter((x) => x.fieldPositions)) {
    const fieldPosition = page.fieldPositions
      .slice()
      .sort(fieldPositionSorter)
      .find((position) => position.fieldId === fieldId && isVisible(formulaValues, position));

    if (fieldPosition) {
      return { page, fieldPosition };
    }
  }
  return null;
}

function getListViewPosition(
  visibleSortedTabs: DataLayoutTab[],
  fieldId: string,
  formulaValues: Record<string, any>
): { tab: DataLayoutTab; section: DataLayoutSection; dataLayoutPosition: DataLayoutPosition } {
  for (const tab of visibleSortedTabs.filter((x) => x.sections)) {
    const visibleSections = tab.sections
      .filter((section) => isVisible(formulaValues, section))
      .slice()
      .sort((a, b) => a.sortNumber - b.sortNumber);
    for (const section of visibleSections.filter((x) => x.positions)) {
      const dataLayoutPosition = section.positions
        .slice()
        .sort((a, b) => a.sortNumber - b.sortNumber)
        .find((position) => position.fieldId === fieldId && isVisible(formulaValues, position));

      if (dataLayoutPosition) {
        return { tab, section, dataLayoutPosition };
      }
    }
  }
  return null;
}

export function getViewPosition(
  view: 'list' | 'form',
  visibleSortedTabs: DataLayoutTab[],
  visibleSortedPages: TemplatePage[],
  field: TemplateField,
  formulaValues: Record<string, any>,
  isPublicForm: boolean
): ViewPosition {
  if (view === 'form') {
    const formViewLocation = getFormViewPosition(visibleSortedPages, field?.id, formulaValues);
    if (formViewLocation != null) {
      return {
        ...formViewLocation,
        field,
        groupType: 'primary',
        groupName: `Page ${getVisiblePageNumber(formViewLocation.page, visibleSortedPages)}`
      };
    }
    if (!isPublicForm) {
      const listViewLocation = getListViewPosition(visibleSortedTabs, field?.id, formulaValues);
      if (listViewLocation != null) {
        return {
          ...listViewLocation,
          field,
          groupType: 'secondary',
          groupName: listViewLocation.tab.name
        };
      }
    }
  } else {
    const listViewLocation = getListViewPosition(visibleSortedTabs, field?.id, formulaValues);
    if (listViewLocation != null) {
      return {
        ...listViewLocation,
        field,
        groupType: 'primary',
        groupName: listViewLocation.tab.name
      };
    }
    if (!isPublicForm) {
      const formViewLocation = getFormViewPosition(visibleSortedPages, field?.id, formulaValues);
      if (formViewLocation != null) {
        return {
          ...formViewLocation,
          field,
          groupType: 'secondary',
          groupName: `Page ${getVisiblePageNumber(formViewLocation.page, visibleSortedPages)}`
        };
      }
    }
  }

  return { field, groupType: 'unmapped', groupName: 'Unmapped' };
}
